import React, { useEffect, useState } from 'react';
import api from './api';
import UserModal from './UserModal';
import ResetPasswordModal from './ResetPasswordModal';
import AbsencesPanel from './AbsencesPanel';
import MaintenancePanel from './MaintenancePanel';
import { CAN_RESET_PASSWORD_ROLES, formatDate, ROLE_LABELS, userDisplayName, userInitials, peutFaireMaintenance } from './helpers';


function AccessManagement({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState(undefined); // undefined = fermé, null = nouveau
  const [resetUser, setResetUser] = useState(null);
  const [absencesUser, setAbsencesUser] = useState(null);
  const [organisation, setOrganisation] = useState(null);
  const [showMaintenance, setShowMaintenance] = useState(false);

  // Un directeur administre les comptes ; un chef de projet peut uniquement
  // dépanner ses collègues en réinitialisant un mot de passe.
  const estDirecteur = currentUser?.role === 'Director';
  const peutReinitialiser = CAN_RESET_PASSWORD_ROLES.includes(currentUser?.role);

  const load = () => {
    setLoading(true);
    api.get('/users').then((res) => setUsers(res.data)).catch(() => setError('Impossible de charger les utilisateurs.')).finally(() => setLoading(false));
  };

  const chargerOrganisation = () => {
    api.get('/organisation').then((res) => setOrganisation(res.data)).catch(() => {});
  };

  useEffect(load, []);
  useEffect(chargerOrganisation, []);

  // Chef de service et signature direction décident de qui valide quoi :
  // le serveur n'accepte ces changements que de la part de la direction.
  const basculerDroit = async (u, champ) => {
    setError('');
    try {
      await api.put(`/users/${u.id}`, { [champ]: !u[champ] });
      load();
      chargerOrganisation();
    } catch (err) {
      setError(err.response?.data?.message || 'Modification impossible.');
    }
  };

  const handleToggleActive = async (u) => {
    try {
      if (u.active) {
        await api.delete(`/users/${u.id}`);
      } else {
        await api.put(`/users/${u.id}`, { active: true });
      }
      load();
    } catch (err) {
      setError("Impossible de mettre à jour le compte.");
    }
  };

  const handleSaved = () => {
    setEditingUser(undefined);
    load();
  };

  const handleDeleteProfile = async (u) => {
    if (!window.confirm(`Supprimer définitivement le profil de ${userDisplayName(u)} ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/users/${u.id}/permanent`);
      load();
    } catch (err) {
      setError('Impossible de supprimer ce profil.');
    }
  };

  if (loading) return <p>Chargement...</p>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="fade-in">
      <div className="page-header-row">
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', maxWidth: 640 }}>
          Chaque utilisateur change son propre mot de passe depuis son profil (clic sur son nom, en bas à gauche).
          {peutReinitialiser && ' En cas d’oubli, vous pouvez en attribuer un nouveau sans connaître l’ancien.'}
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Plus restreint que la direction : seul le compte d'administration
              accède aux opérations irréversibles. */}
          {peutFaireMaintenance(currentUser) && (
            <button className="btn" onClick={() => setShowMaintenance(true)}>Maintenance du portefeuille</button>
          )}
          {estDirecteur && (
            <button className="btn btn-primary" onClick={() => setEditingUser(null)}>+ Nouvel utilisateur</button>
          )}
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Personne</th>
              <th>Email</th>
              <th>Rôle</th>
              <th>Département</th>
              <th>Chef de service</th>
              <th>Signe direction</th>
              <th>Rotation</th>
              <th>Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="avatar">{userInitials(u)}</div>
                    <div>
                      <div style={{ fontWeight: 500 }}>{userDisplayName(u)}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {u.username}
                        {!u.full_name && <span title="Nom complet à renseigner via « Modifier »"> · nom complet manquant</span>}
                      </div>
                      {u.password_reset_at && (
                        <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                          mdp réinitialisé le {formatDate(u.password_reset_at)}
                          {u.password_reset_by ? ` par ${u.password_reset_by}` : ''}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td>{u.email}</td>
                <td>{ROLE_LABELS[u.role] || u.role}</td>
                <td>{u.department}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={!!u.est_chef_de_service}
                    disabled={!estDirecteur}
                    onChange={() => basculerDroit(u, 'est_chef_de_service')}
                    title="Dirige son service et valide les chartes qui le concernent"
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={!!u.valide_direction}
                    disabled={!estDirecteur}
                    onChange={() => basculerDroit(u, 'valide_direction')}
                    title="Signe au niveau direction d'usines"
                  />
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {(() => {
                    const absent = organisation?.absents?.find((a) => a.user_id === u.id);
                    if (!absent) return <span style={{ color: 'var(--text-muted)' }}>présent</span>;
                    return (
                      <span title={`Jusqu'au ${formatDate(absent.date_fin)}`}>
                        <span className="badge badge-review">absent</span>
                        <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2 }}>
                          {absent.interimaire_name ? `intérim : ${absent.interimaire_name}` : 'sans intérimaire'}
                        </div>
                      </span>
                    );
                  })()}
                </td>
                <td>
                  <span className={`badge ${u.active ? 'badge-active' : 'badge-blocked'}`}>
                    {u.active ? 'Actif' : 'Désactivé'}
                  </span>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {peutReinitialiser && (
                    <>
                      <button className="btn btn-sm" onClick={() => setAbsencesUser(u)}>Rotations</button>{' '}
                      <button className="btn btn-sm" onClick={() => setResetUser(u)}>
                        Réinitialiser le mot de passe
                      </button>{' '}
                    </>
                  )}
                  {estDirecteur && (
                    <>
                      <button className="btn btn-sm" onClick={() => setEditingUser(u)}>Modifier</button>{' '}
                      <button className={`btn btn-sm ${u.active ? 'btn-danger' : ''}`} onClick={() => handleToggleActive(u)}>
                        {u.active ? 'Désactiver' : 'Réactiver'}
                      </button>{' '}
                      <button className="btn btn-sm btn-danger" onClick={() => handleDeleteProfile(u)}>Supprimer</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingUser !== undefined && (
        <UserModal user={editingUser} onClose={() => setEditingUser(undefined)} onSaved={handleSaved} />
      )}

      {resetUser && (
        <ResetPasswordModal
          user={resetUser}
          onClose={() => { setResetUser(null); load(); }}
        />
      )}

      {showMaintenance && (
        <div className="modal-overlay" onClick={() => setShowMaintenance(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Maintenance du portefeuille</h2>
              <button className="modal-close" onClick={() => setShowMaintenance(false)}>×</button>
            </div>
            <MaintenancePanel onTermine={load} />
          </div>
        </div>
      )}

      {absencesUser && (
        <div className="modal-overlay" onClick={() => { setAbsencesUser(null); chargerOrganisation(); }}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Rotations — {userDisplayName(absencesUser)}</h2>
              <button className="modal-close" onClick={() => { setAbsencesUser(null); chargerOrganisation(); }}>×</button>
            </div>
            <AbsencesPanel
              currentUser={currentUser}
              users={users}
              pourUtilisateur={absencesUser}
              compact
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default AccessManagement;
